import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Search, Mic, Play, Pause } from "lucide-react";
import { NAV_LINKS } from "../lib/constants";
import { useBranding } from "../lib/branding";
import { useLiveRadio } from "../lib/liveRadio";
import SearchModal from "./SearchModal";

export default function Header() {
    const { pathname } = useLocation();
    const { logoUrl } = useBranding();
    const { onAir, hasStream, isPlaying, togglePlay } = useLiveRadio();
    const [searchOpen, setSearchOpen] = useState(false);
    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 bg-black border-b border-streetz-line"
        >
            <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
                <Link
                    to="/"
                    data-testid="logo-home-link"
                    className="flex items-center gap-2 shrink-0"
                >
                    <img
                        src={logoUrl}
                        alt="MyStreetz"
                        className="brand-logo h-8 md:h-10 w-auto"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
                    {NAV_LINKS.map((l) => (
                        <Link
                            key={l.label}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`brand-nav overline hover:text-streetz-red transition-colors ${
                                pathname === l.to ? "text-streetz-red" : "text-white"
                            }`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2 md:gap-3">
                    <button
                        aria-label="Ask MyStreetz AI"
                        data-testid="header-search-button"
                        onClick={() => setSearchOpen(true)}
                        className="h-10 px-3 md:px-4 inline-flex items-center gap-2 text-white hover:text-streetz-yellow border border-streetz-line hover:border-streetz-yellow"
                    >
                        <Search className="w-4 h-4" />
                        <span className="hidden md:inline overline text-[10px]">ASK MYSTREETZ</span>
                    </button>
                    <Link
                        to="/submit"
                        data-testid="header-submit-music-cta"
                        className="hidden md:inline-flex items-center gap-2 bg-streetz-red text-white px-5 py-3 overline hover:bg-white hover:text-black transition-colors"
                    >
                        <Mic className="w-4 h-4" /> SUBMIT MUSIC
                    </Link>
                    <button
                        type="button"
                        onClick={togglePlay}
                        disabled={!hasStream}
                        data-testid="header-listen-live-cta"
                        aria-label={isPlaying ? "Pause MyStreetz live radio" : "Play MyStreetz live radio"}
                        title={hasStream ? (isPlaying ? "Pause MyStreetz Radio" : "Play MyStreetz Radio") : "No live stream configured"}
                        className={`inline-flex items-center gap-2 px-4 md:px-5 py-3 overline transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isPlaying
                                ? "bg-streetz-red text-white hover:bg-white hover:text-black"
                                : "bg-streetz-yellow text-black hover:bg-white"
                        }`}
                    >
                        {isPlaying ? (
                            <>
                                <Pause className="w-4 h-4" />
                                <span className="hidden sm:inline">PAUSE</span>
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 ml-[2px]" />
                                <span className="hidden sm:inline">LISTEN TO LIVE RADIO</span>
                            </>
                        )}
                        {onAir && !isPlaying && (
                            <span className="hidden md:inline relative flex h-2 w-2 ml-1">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-streetz-red opacity-75 animate-pulse-red" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-streetz-red" />
                            </span>
                        )}
                    </button>
                </div>
            </div>
            <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
        </header>
    );
}
